import { Listbox } from "@headlessui/react";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { zenbridge } from "../api/zenbridgeClient";

const MainContent = ({ loading, contentData, refreshData, selectedTenant }) => {
  const [lastRefreshed, setLastRefreshed] = useState();
  const [eventRules, setEventRules] = useState("");
  const [volumeRules, setVolumeRules] = useState("");
  const [isVolumeModalOpen, setIsVolumeModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);

  // Pagination states
  const [currentPageVolume, setCurrentPageVolume] = useState(1);
  const [currentPageWorkflow, setCurrentPageWorkflow] = useState(1);
  const [currentPageVolumeRules, setCurrentPageVolumeRules] = useState(1);
  const [currentPageWorkflowRules, setCurrentPageWorkflowRules] = useState(1);
  // Items per page
  const [volumeValidationItemsPerPage, setVolumeValidationItemsPerPage] =
    useState(25);
  const [workflowValidationItemsPerPage, setWorkflowValidationItemsPerPage] =
    useState(25);
  const [volumeRulesItemsPerPage, setVolumeRulesItemsPerPage] = useState(25);
  const [workflowRulesItemsPerPage, setWorkflowRulesItemsPerPage] =
    useState(25);

  const [itemsPerPageOptions] = useState([10, 25, 50, 100]);
  const [fetchedVolumeRules, setFetchedVolumeRules] = useState();
  const [fetchedWorkflowRules, setFetchedWorkflowRules] = useState();
  const [viewVolumeRules, setViewVolumeRules] = useState(true); // Default to show
  const [viewWorkflowRules, setViewWorkflowRules] = useState(true); // Default to show

  const [isPostingRules, setIsPostingRules] = useState(false);
  const [rulesResponse, setRulesResponse] = useState({
    label: "",
    status: "",
    resp: "",
  });

  // Filter states
  const [filteredVolumeData, setFilteredVolumeData] = useState([]);
  const [filteredWorkflowData, setFilteredWorkflowData] = useState([]);
  const [filteredVolumeRules, setFilteredVolumeRules] = useState([]);
  const [filteredWorkflowRules, setFilteredWorkflowRules] = useState([]);

  const [volumeFilter, setVolumeFilter] = useState("");
  const [workflowFilter, setWorkflowFilter] = useState("");
  const [volumeRulesFilter, setVolumeRulesFilter] = useState("");
  const [workflowRulesFilter, setWorkflowRulesFilter] = useState("");

  const [activeTab, setActiveTab] = useState("volume");
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMsg, setUpdateMsg] = useState({ id: "", status: "", msg: "" });
  const [volumeUpdateMsg, setVolumeUpdateMsg] = useState({
    id: "",
    status: "",
    msg: "",
  });

  const [filterPartnerVolume, setFilterPartnerVolume] = useState("All");
  const [filterPartnerWorkflow, setFilterPartnerWorkflow] = useState("All");

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { item, action, index, type }

  // Effects
  useEffect(() => {
    if (contentData) {
      const dateTime = contentData.refreshedDateTime;
      setLastRefreshed(timeAgo(dateTime));
    }
  }, [contentData]);

  useEffect(() => {
    setFetchedVolumeRules("");
    setFetchedWorkflowRules("");
    setViewVolumeRules(true);
    setViewWorkflowRules(true);
    getVolumeRules();
    getWorkflowRules();
    setVolumeRules("");
    setEventRules("");
    // Reset filters on tenant change to avoid stale data
    setFilterPartnerVolume("All");
    setFilterPartnerWorkflow("All");
    setVolumeFilter("");
    setWorkflowFilter("");
    setVolumeRulesFilter("");
    setWorkflowRulesFilter("");
  }, [selectedTenant]);

  // Filtered data effects
  useEffect(() => {
    let data = contentData?.volumeValidation || [];
    if (filterPartnerVolume !== "All") {
      data = data.filter((item) => item.partnerId === filterPartnerVolume);
    }
    if (volumeFilter.trim()) {
      data = data.filter((item) => {
        return (
          (item.partnerId &&
            item.partnerId
              .toLowerCase()
              .includes(volumeFilter.toLowerCase())) ||
          (item.eventName &&
            item.eventName.toLowerCase().includes(volumeFilter.toLowerCase()))
        );
      });
    }
    setFilteredVolumeData(data);
  }, [contentData, filterPartnerVolume, volumeFilter]);

  useEffect(() => {
    let data =
      contentData?.workflowValidation?.workflowDataValidationResult || [];
    if (filterPartnerWorkflow !== "All") {
      data = data.filter((item) => item.partnerId === filterPartnerWorkflow);
    }
    if (workflowFilter.trim()) {
      data = data.filter((item) => {
        return (
          (item.partnerId &&
            item.partnerId
              .toLowerCase()
              .includes(workflowFilter.toLowerCase())) ||
          (item.referenceId &&
            item.referenceId
              .toLowerCase()
              .includes(workflowFilter.toLowerCase()))
        );
      });
    }
    setFilteredWorkflowData(data);
  }, [contentData, filterPartnerWorkflow, workflowFilter]);

  useEffect(() => {
    let data = fetchedVolumeRules || [];
    if (filterPartnerVolume !== "All") {
      data = data.filter((item) => item.partnerId === filterPartnerVolume);
    }
    if (volumeRulesFilter.trim()) {
      data = data.filter((item) => {
        return (
          (item.partnerId &&
            item.partnerId
              .toLowerCase()
              .includes(volumeRulesFilter.toLowerCase())) ||
          (item.eventName &&
            item.eventName
              .toLowerCase()
              .includes(volumeRulesFilter.toLowerCase()))
        );
      });
    }
    setFilteredVolumeRules(data);
  }, [fetchedVolumeRules, filterPartnerVolume, volumeRulesFilter]);

  useEffect(() => {
    let data = fetchedWorkflowRules || [];
    if (filterPartnerWorkflow !== "All") {
      data = data.filter((item) => item.partnerId === filterPartnerWorkflow);
    }
    if (workflowRulesFilter.trim()) {
      data = data.filter((item) => {
        return (
          (item.partnerId &&
            item.partnerId
              .toLowerCase()
              .includes(workflowRulesFilter.toLowerCase())) ||
          (item.event_1 &&
            item.event_1
              .toLowerCase()
              .includes(workflowRulesFilter.toLowerCase())) ||
          (item.event_2 &&
            item.event_2
              .toLowerCase()
              .includes(workflowRulesFilter.toLowerCase()))
        );
      });
    }
    setFilteredWorkflowRules(data);
  }, [fetchedWorkflowRules, filterPartnerWorkflow, workflowRulesFilter]);

  // Data
  const volumeValidationData = contentData?.volumeValidation || [];
  const workflowValidationData =
    contentData?.workflowValidation?.workflowDataValidationResult || [];

  // Modal handlers
  const openVolumeModal = () => {
    setIsVolumeModalOpen(true);
    document.body.style.overflow = "hidden";
  };
  const openEventModal = () => {
    setIsEventModalOpen(true);
    document.body.style.overflow = "hidden";
  };
  const closeSidebar = (label) => {
    if (label === "volume") setIsVolumeModalOpen(false);
    else setIsEventModalOpen(false);
    setRulesResponse({ label: "", resp: "", status: "" });
    document.body.style.overflow = "auto";
  };

  // Rule submission
  const handleChange = (event, name) => {
    if (name === "volume") setVolumeRules(event.target.value);
    else setEventRules(event.target.value);
  };

  const handleSubmittingRules = (type) => {
    const rules = type === "volume" ? volumeRules : eventRules;
    handlePostRequest(rules, type);
  };

  const handlePostRequest = async (rules, label) => {
    setIsPostingRules(true);
    const endpoint =
      label === "volume"
        ? "/event_data_validator/volume_based_sla"
        : "/event_data_validator/workflow_based_sla";
    try {
      const data = await zenbridge.post(endpoint, {
        tenantId: selectedTenant,
        slaInstructions: String(rules || "").replace(/\n/g, "\\n"),
      });
      setRulesResponse({ label, status: "success", resp: data });
      toast.success("Rules added successfully.");
    } catch (error) {
      setRulesResponse({
        label,
        status: "failed",
        resp: error?.message || String(error),
      });
      toast.error("Failed to add rules.");
    } finally {
      setIsPostingRules(false);
    }
  };

  // Data fetching
  const getVolumeRules = async () => {
    try {
      const data = await zenbridge.get(
        `/event_data_validator/volume_based_sla_rule?tenantId=${selectedTenant}`
      );
      if (Array.isArray(data?.events)) {
        setFetchedVolumeRules(data.events);
      } else {
        toast.error("Unexpected data format for volume rules.");
      }
    } catch (error) {
      toast.error(error?.message || "Volume rules fetching failed!");
    }
  };

  const getWorkflowRules = async () => {
    try {
      const data = await zenbridge.get(
        `/event_data_validator/workflow_based_sla_rule?tenantId=${selectedTenant}`
      );
      if (Array.isArray(data?.events)) {
        setFetchedWorkflowRules(data.events);
      }
    } catch (error) {
      toast.error(error?.message || "Workflow rules fetching failed!");
    }
  };

  const requestActionConfirmation = (item, action, index, type) => {
    setPendingAction({ item, action, index, type });
    setConfirmModalOpen(true);
  };

  // Action handlers
  const handleActionClick = async (item, action, index) => {
    setIsUpdating(true);
    try {
      const data = await zenbridge.put(
        "/event_data_validator/update/event_data_status",
        {
          tenantId: selectedTenant,
          partnerId: item.partnerId,
          referenceNo: item.referenceId,
          eventDataStatus: action,
        }
      );
      setFilteredWorkflowData((prev) =>
        prev.filter(
          (wfItem) =>
            !(
              wfItem.partnerId === item.partnerId &&
              wfItem.referenceId === item.referenceId
            )
        )
      );
      setUpdateMsg({ id: index, status: "true", msg: "" });
      toast.success("Event status updated successfully!");
      return data;
    } catch (error) {
      const msg = error?.message || String(error);
      setUpdateMsg({ id: index, status: "false", msg });
      toast.error(`Error: ${msg}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleVolumeActionClick = async (item, action, index) => {
    setIsUpdating(true);
    try {
      const data = await zenbridge.put(
        "/event_data_validator/update/volume_data_status",
        {
          tenantId: selectedTenant,
          partnerId: item.partnerId,
          eventName: item.eventName,
          eventDataStatus: action,
        }
      );
      setFilteredVolumeData((prev) =>
        prev.filter(
          (vItem) =>
            !(
              vItem.partnerId === item.partnerId &&
              vItem.eventName === item.eventName
            )
        )
      );
      setVolumeUpdateMsg({ id: index, status: "true", msg: "" });
      toast.success("Volume status updated successfully!");
      return data;
    } catch (error) {
      const msg = error?.message || String(error);
      setVolumeUpdateMsg({ id: index, status: "false", msg });
      toast.error(`Error: ${msg}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Pagination logic
  const handlePageChange = (page, type) => {
    switch (type) {
      case "volume":
        setCurrentPageVolume(page);
        break;
      case "workflow":
        setCurrentPageWorkflow(page);
        break;
      case "volumeRules":
        setCurrentPageVolumeRules(page);
        break;
      case "workflowRules":
        setCurrentPageWorkflowRules(page);
        break;
      default:
        break;
    }
  };

  // Pagination calculations
  const totalVolumePages = Math.ceil(
    (filteredVolumeData?.length || 0) / volumeValidationItemsPerPage
  );
  const totalWorkflowPages = Math.ceil(
    (filteredWorkflowData?.length || 0) / workflowValidationItemsPerPage
  );
  const totalVolumeRulesPages = Math.ceil(
    (filteredVolumeRules?.length || 0) / volumeRulesItemsPerPage
  );
  const totalWorkflowRulesPages = Math.ceil(
    (filteredWorkflowRules?.length || 0) / workflowRulesItemsPerPage
  );

  const startIndexVolume =
    (currentPageVolume - 1) * volumeValidationItemsPerPage;
  const endIndexVolume = startIndexVolume + volumeValidationItemsPerPage;
  const currentVolumeData = filteredVolumeData.slice(
    startIndexVolume,
    endIndexVolume
  );

  const startIndexWorkflow =
    (currentPageWorkflow - 1) * workflowValidationItemsPerPage;
  const endIndexWorkflow = startIndexWorkflow + workflowValidationItemsPerPage;
  const currentWorkflowData = filteredWorkflowData.slice(
    startIndexWorkflow,
    endIndexWorkflow
  );

  const startIndexVolumeRules =
    (currentPageVolumeRules - 1) * volumeRulesItemsPerPage;
  const endIndexVolumeRules = startIndexVolumeRules + volumeRulesItemsPerPage;
  const currentVolumeRulesData = filteredVolumeRules
    ? filteredVolumeRules.slice(startIndexVolumeRules, endIndexVolumeRules)
    : [];

  const startIndexWorkflowRules =
    (currentPageWorkflowRules - 1) * workflowRulesItemsPerPage;
  const endIndexWorkflowRules =
    startIndexWorkflowRules + workflowRulesItemsPerPage;
  const currentWorkflowRulesData = filteredWorkflowRules
    ? filteredWorkflowRules.slice(startIndexWorkflowRules, endIndexWorkflowRules)
    : [];

  function timeAgo(refreshedDateTime) {
    const refreshedDate = new Date(refreshedDateTime);
    const istOffset = 5.5 * 60 * 60 * 1000;
    const refreshedDateInIST = new Date(refreshedDate.getTime() + istOffset);
    const currentDate = new Date();
    const diffInMs = currentDate - refreshedDateInIST;
    const diffInMinutes = Math.floor(diffInMs / 1000 / 60);

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes === 1) return "1 minute ago";
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours === 1) return "1 hour ago";
    if (diffInHours < 24) return `${diffInHours} hours ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "1 day ago";
    return `${diffInDays} days ago`;
  }

  useEffect(() => {
    setCurrentPageVolume(1);
    setCurrentPageWorkflow(1);
    setCurrentPageVolumeRules(1);
    setCurrentPageWorkflowRules(1);
  }, [
    totalVolumePages,
    totalWorkflowPages,
    totalVolumeRulesPages,
    totalWorkflowRulesPages,
  ]);

  // Render helpers
  const PaginationControls = ({
    currentPage,
    totalPages,
    onPageChange,
    itemsPerPage,
    onItemsPerPageChange,
    totalItems,
    startIndex,
  }) => (
    <div className="flex items-center justify-end gap-4 text-sm text-gray-600">
      <span>Showing</span>
      <Select
        value={itemsPerPage}
        onChange={onItemsPerPageChange}
        options={itemsPerPageOptions}
      />
      <span className="w-[93px]">
        {startIndex + 1} - {Math.min(startIndex + itemsPerPage, totalItems)} of{" "}
        {totalItems}
      </span>
      <div className="flex">
        <button
          onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1}
          className="border px-2 py-2.5 border-gray-300 rounded-l hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="6"
            height="10"
            viewBox="0 0 6 10"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.70711 0.292893C6.09763 0.683417 6.09763 1.31658 5.70711 1.70711L2.41421 5L5.70711 8.29289C6.09763 8.68342 6.09763 9.31658 5.70711 9.70711C5.31658 10.0976 4.68342 10.0976 4.29289 9.70711L0.292893 5.70711C-0.0976311 5.31658 -0.0976311 4.68342 0.292893 4.29289L4.29289 0.292893C4.68342 -0.0976311 5.31658 -0.0976311 5.70711 0.292893Z"
              fill="#6B7280"
            />
          </svg>
        </button>
        <button
          onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages || totalPages === 0}
          className=" border-r border-y border-gray-300 px-2 py-2.5 rounded-r hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="6"
            height="10"
            viewBox="0 0 6 10"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.292894 9.70711C-0.0976307 9.31658 -0.0976307 8.68342 0.292894 8.29289L3.58579 5L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683418 0.292893 0.292894C0.683417 -0.0976312 1.31658 -0.0976312 1.70711 0.292894L5.70711 4.29289C6.09763 4.68342 6.09763 5.31658 5.70711 5.70711L1.70711 9.70711C1.31658 10.0976 0.683418 10.0976 0.292894 9.70711Z"
              fill="#6B7280"
            />
          </svg>
        </button>
      </div>
    </div>
  );

  const Select = ({ value, onChange, options, label, className }) => {
    return (
      <Listbox value={value} onChange={onChange}>
        {label && (
          <Listbox.Label className="block text-sm font-medium text-gray-500  m-1">
            {label}
          </Listbox.Label>
        )}

        <div className={`relative ${className || ""}`}>
          <Listbox.Button className="w-full cursor-default rounded-md border border-gray-300 bg-white py-1.5 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 sm:text-sm">
            <span className="block truncate text-sm">{value}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                />
              </svg>
            </span>
          </Listbox.Button>

          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {options.map((option, index) => (
              <Listbox.Option
                key={index}
                value={option}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-4 pr-4 ${
                    active ? "bg-purple-100 text-purple-900" : "text-gray-900"
                  }`
                }
              >
                {({ selected }) => (
                  <span
                    className={`block truncate ${
                      selected ? "font-semibold" : "font-normal"
                    }`}
                  >
                    {option}
                  </span>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      </Listbox>
    );
  };

  const getVolumePartners = () => {
    const partners = new Set();
    if (volumeValidationData && volumeValidationData.length > 0) {
      volumeValidationData.forEach((item) => {
        if (item.partnerId) partners.add(item.partnerId);
      });
    }
    if (fetchedVolumeRules && fetchedVolumeRules.length > 0) {
      fetchedVolumeRules.forEach((item) => {
        if (item.partnerId) partners.add(item.partnerId);
      });
    }
    return ["All", ...Array.from(partners).sort()];
  };

  const getWorkflowPartners = () => {
    const partners = new Set();
    if (workflowValidationData && workflowValidationData.length > 0) {
      workflowValidationData.forEach((item) => {
        if (item.partnerId) partners.add(item.partnerId);
      });
    }
    if (fetchedWorkflowRules && fetchedWorkflowRules.length > 0) {
      fetchedWorkflowRules.forEach((item) => {
        if (item.partnerId) partners.add(item.partnerId);
      });
    }
    return ["All", ...Array.from(partners).sort()];
  };

  const volumePartnerOptions = getVolumePartners();
  const workflowPartnerOptions = getWorkflowPartners();

  const handleFilterPartnerVolume = (val) => {
    setFilterPartnerVolume(val);
    setCurrentPageVolume(1);
    setCurrentPageVolumeRules(1);
  };

  const handleFilterPartnerWorkflow = (val) => {
    setFilterPartnerWorkflow(val);
    setCurrentPageWorkflow(1);
    setCurrentPageWorkflowRules(1);
  };

  const handleVolumeRulesFilterChange = (e) => {
    setVolumeRulesFilter(e.target.value);
  };

  const handleVolumeFilterChange = (e) => {
    setVolumeFilter(e.target.value);
  };

  const handleWorkflowRulesFilterChange = (e) => {
    setWorkflowRulesFilter(e.target.value);
  };

  const handleWorkflowFilterChange = (e) => {
    setWorkflowFilter(e.target.value);
  };

  return (
    <div className=" overflow-y-auto bg-gray-50 p-4">
      <div className="flex justify-between items-center mb-6">
        <h4 className="text-[16px] font-semibold text-gray-800 mt-2.5">
          SLA Data for{" "}
          <span className="text-purple-600 capitalize">{selectedTenant}</span>
        </h4>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span>Last Refreshed: {lastRefreshed || "..."}</span>
          <button
            onClick={() => refreshData()}
            disabled={loading}
            className="p-2 rounded-md hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 border"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className={`size-5 ${loading ? "animate-spin" : ""}`}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-end gap-8 border-b-2 border-gray-200  mb-6">
        <button
          onClick={() => setActiveTab("volume")}
          className={`pt-2 pb-4  text-sm font-medium ${
            activeTab === "volume"
              ? "border-b-2 border-purple-600 text-purple-600"
              : "text-gray-500 hover:text-gray-700 border-b-2 border-gray-50"
          }`}
        >
          Volume
        </button>
        <button
          onClick={() => setActiveTab("workflow")}
          className={`pt-2 pb-4  text-sm font-medium ${
            activeTab === "workflow"
              ? "border-b-2 border-purple-600 text-purple-600"
              : "text-gray-500 hover:text-gray-700 border-b-2 border-gray-50"
          }`}
        >
          Workflow
        </button>
        <div className="flex-grow" />
        <div className="flex  items-center justify-center mb-4">
          <Select
            options={
              activeTab === "volume" ? volumePartnerOptions : workflowPartnerOptions
            }
            value={
              activeTab === "volume" ? filterPartnerVolume : filterPartnerWorkflow
            }
            label="Filter Partner"
            onChange={(e) =>
              activeTab === "volume"
                ? handleFilterPartnerVolume(e)
                : handleFilterPartnerWorkflow(e)
            }
            className="w-40"
          />
        </div>
        <button
          onClick={() =>
            activeTab === "volume"
              ? setViewVolumeRules(!viewVolumeRules)
              : setViewWorkflowRules(!viewWorkflowRules)
          }
          className="border mb-4 w-[6rem] border-[#D1D5DB] text-sm font-medium text-gray-700 hover:text-gray-700 px-2 py-1.5 rounded-md bg-white hover:bg-gray-50"
        >
          {activeTab === "volume"
            ? viewVolumeRules
              ? "Hide Rules"
              : "Show Rules"
            : viewWorkflowRules
              ? "Hide Rules"
              : "Show Rules"}
        </button>
        <button
          onClick={activeTab === "volume" ? openVolumeModal : openEventModal}
          className="mb-4 flex items-center gap-2 bg-purple-600 text-white px-4 py-1.5 rounded-md text-sm font-medium hover:bg-purple-700"
        >
          Add Rules
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading data...</div>
      ) : !contentData ? (
        <div className="text-center py-10 text-gray-500">No Data Available</div>
      ) : (
        <div>
          {/* Volume Tab */}
          {activeTab === "volume" && (
            <div className="space-y-8">
              {viewVolumeRules && (
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-[16px] font-semibold text-gray-800">
                      Volume Rules
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="rounded border border-gray-300 relative">
                        <input
                          className=" pl-12 pr-4 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded placeholder:text-sm text-gray-600"
                          onChange={handleVolumeRulesFilterChange}
                          placeholder="Search by Partner ID or Event"
                          value={volumeRulesFilter}
                        />
                        <svg
                          className="absolute inset-0 top-2 left-3"
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M8 4C5.79086 4 4 5.79086 4 8C4 10.2091 5.79086 12 8 12C10.2091 12 12 10.2091 12 8C12 5.79086 10.2091 4 8 4ZM2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 9.29583 13.5892 10.4957 12.8907 11.4765L17.7071 16.2929C18.0976 16.6834 18.0976 17.3166 17.7071 17.7071C17.3166 18.0976 16.6834 18.0976 16.2929 17.7071L11.4765 12.8907C10.4957 13.5892 9.29583 14 8 14C4.68629 14 2 11.3137 2 8Z"
                            fill="#9CA3AF"
                          />
                        </svg>
                      </div>
                      <PaginationControls
                        currentPage={currentPageVolumeRules}
                        totalPages={totalVolumeRulesPages}
                        onPageChange={(p) => handlePageChange(p, "volumeRules")}
                        itemsPerPage={volumeRulesItemsPerPage}
                        onItemsPerPageChange={setVolumeRulesItemsPerPage}
                        totalItems={filteredVolumeRules?.length || 0}
                        startIndex={startIndexVolumeRules}
                      />
                    </div>
                  </div>
                  <div className="overflow-auto h-[20vh]">
                    <table className="w-full text-sm text-left text-gray-600 border rounded">
                      <thead className="text-xs text-gray-900 font-medium uppercase bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-6 py-3 sticky top-0">S. No.</th>
                          <th className="px-6 py-3 sticky top-0">Partner ID</th>
                          <th className="px-6 py-3 sticky top-0">Event</th>
                          <th className="px-6 py-3 sticky top-0">
                            Period Start Range
                          </th>
                          <th className="px-6 py-3 sticky top-0">
                            Period End Range
                          </th>
                          <th className="px-6 py-3 sticky top-0">Period Time</th>
                          <th className="px-6 py-3 sticky top-0">Period Unit</th>
                        </tr>
                      </thead>
                      <tbody className="border rounded">
                        {currentVolumeRulesData.length > 0 ? (
                          currentVolumeRulesData.map((item, i) => (
                            <tr
                              key={i}
                              className="bg-white border-b hover:bg-gray-50"
                            >
                              <td className="px-6 py-4">
                                {startIndexVolumeRules + i + 1}
                              </td>
                              <td className="px-6 py-4">{item.partnerId}</td>
                              <td className="px-6 py-4">{item.eventName}</td>
                              <td className="px-6 py-4">
                                {item.period?.startRange}
                              </td>
                              <td className="px-6 py-4">
                                {item.period?.endRange}
                              </td>
                              <td className="px-6 py-4">{item.period?.time}</td>
                              <td className="px-6 py-4">{item.period?.unit}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7} className="text-center py-4">
                              No volume rules found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-[16px] font-semibold text-gray-800">
                    Volume Abnormalities
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="rounded border relative">
                      <input
                        className=" pl-12 pr-4 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded placeholder:text-sm "
                        onChange={handleVolumeFilterChange}
                        placeholder="Search by Partner ID or Event"
                        value={volumeFilter}
                      />
                      <svg
                        className="absolute inset-0 top-2 left-3"
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M8 4C5.79086 4 4 5.79086 4 8C4 10.2091 5.79086 12 8 12C10.2091 12 12 10.2091 12 8C12 5.79086 10.2091 4 8 4ZM2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 9.29583 13.5892 10.4957 12.8907 11.4765L17.7071 16.2929C18.0976 16.6834 18.0976 17.3166 17.7071 17.7071C17.3166 18.0976 16.6834 18.0976 16.2929 17.7071L11.4765 12.8907C10.4957 13.5892 9.29583 14 8 14C4.68629 14 2 11.3137 2 8Z"
                          fill="#9CA3AF"
                        />
                      </svg>
                    </div>
                    <PaginationControls
                      currentPage={currentPageVolume}
                      totalPages={totalVolumePages}
                      onPageChange={(p) => handlePageChange(p, "volume")}
                      itemsPerPage={volumeValidationItemsPerPage}
                      onItemsPerPageChange={setVolumeValidationItemsPerPage}
                      totalItems={filteredVolumeData.length}
                      startIndex={startIndexVolume}
                    />
                  </div>
                </div>

                <div className="overflow-auto">
                  <table className="w-full text-sm text-left text-gray-600 ">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0 border rounded-lg">
                      <tr>
                        <th className="px-6 py-3 sticky top-0">S. No.</th>
                        <th className="px-6 py-3 sticky top-0">Event</th>
                        <th className="px-6 py-3 sticky top-0">Partner ID</th>
                        <th className="px-6 py-3 sticky top-0 w-1/2">
                          Comments
                        </th>
                        <th className="px-6 py-3 sticky top-0 text-center">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="border rounded">
                      {currentVolumeData.length > 0 ? (
                        currentVolumeData.map((item, i) => (
                          <tr
                            key={i}
                            className="bg-white border-b hover:bg-gray-50"
                          >
                            <td className="px-6 py-4">
                              {startIndexVolume + i + 1}
                            </td>
                            <td className="px-6 py-4">{item.eventName}</td>
                            <td className="px-6 py-4">{item.partnerId}</td>
                            <td className="px-6 py-4">
                              <ul className="list-disc list-inside">
                                {String(item.comments || "")
                                  .split(".")
                                  .map((line, idx) =>
                                    line.trim() ? (
                                      <li key={idx}>{line.trim()}.</li>
                                    ) : null
                                  )}
                              </ul>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() =>
                                    requestActionConfirmation(
                                      item,
                                      "Ignore",
                                      i,
                                      "abnormality"
                                    )
                                  }
                                  disabled={isUpdating}
                                  className="p-1 rounded-md disabled:opacity-50 border"
                                  title="Ignore"
                                >
                                  Ignore
                                </button>
                                <button
                                  onClick={() =>
                                    requestActionConfirmation(
                                      item,
                                      "Close",
                                      i,
                                      "abnormality"
                                    )
                                  }
                                  disabled={isUpdating}
                                  className="p-1 rounded-md border disabled:opacity-50"
                                  title="Close"
                                >
                                  Close
                                </button>
                                <button
                                  onClick={() =>
                                    requestActionConfirmation(
                                      item,
                                      "Incorrect",
                                      i,
                                      "abnormality"
                                    )
                                  }
                                  disabled={isUpdating}
                                  className="p-1 rounded-md border disabled:opacity-50"
                                  title="Incorrect"
                                >
                                  Incorrect
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center py-4">
                            No volume abnormalities found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Workflow Tab */}
          {activeTab === "workflow" && (
            <div className="space-y-8">
              {viewWorkflowRules && (
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-[16px] font-semibold text-gray-800">
                      Workflow Rules
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="rounded border relative">
                        <input
                          className=" pl-12 pr-4 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded placeholder:text-sm"
                          onChange={handleWorkflowRulesFilterChange}
                          placeholder="Search by Partner ID or Event"
                          value={workflowRulesFilter}
                        />
                      </div>
                      <PaginationControls
                        currentPage={currentPageWorkflowRules}
                        totalPages={totalWorkflowRulesPages}
                        onPageChange={(p) =>
                          handlePageChange(p, "workflowRules")
                        }
                        itemsPerPage={workflowRulesItemsPerPage}
                        onItemsPerPageChange={setWorkflowRulesItemsPerPage}
                        totalItems={filteredWorkflowRules?.length || 0}
                        startIndex={startIndexWorkflowRules}
                      />
                    </div>
                  </div>
                  <div className="overflow-auto h-[34vh]">
                    <table className="w-full text-sm text-left text-gray-600">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0 border rounded">
                        <tr>
                          <th className="px-6 py-3 sticky top-0">S. No.</th>
                          <th className="px-6 py-3 sticky top-0">Partner ID</th>
                          <th className="px-6 py-3 sticky top-0">Event 1</th>
                          <th className="px-6 py-3 sticky top-0">Event 2</th>
                          <th className="px-6 py-3 sticky top-0">Time Gap</th>
                        </tr>
                      </thead>
                      <tbody className="border rounded">
                        {currentWorkflowRulesData.length > 0 ? (
                          currentWorkflowRulesData.map((item, i) => (
                            <tr
                              key={i}
                              className="bg-white border-b hover:bg-gray-50"
                            >
                              <td className="px-6 py-4">
                                {startIndexWorkflowRules + i + 1}
                              </td>
                              <td className="px-6 py-4">{item.partnerId}</td>
                              <td className="px-6 py-4">{item.event_1}</td>
                              <td className="px-6 py-4">{item.event_2}</td>
                              <td className="px-6 py-4 border">
                                {item.time_gap_number} {item.time_gap_unit}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="text-center py-4">
                              No workflow rules found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-[16px] font-semibold text-gray-800">
                    Workflow Abnormalities
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="rounded border relative">
                      <input
                        className=" pl-12 pr-4 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded placeholder:text-sm"
                        onChange={handleWorkflowFilterChange}
                        placeholder="Search by Partner ID or Event"
                        value={workflowFilter}
                      />
                    </div>
                    <PaginationControls
                      currentPage={currentPageWorkflow}
                      totalPages={totalWorkflowPages}
                      onPageChange={(p) => handlePageChange(p, "workflow")}
                      itemsPerPage={workflowValidationItemsPerPage}
                      onItemsPerPageChange={setWorkflowValidationItemsPerPage}
                      totalItems={filteredWorkflowData.length}
                      startIndex={startIndexWorkflow}
                    />
                  </div>
                </div>
                <div className="overflow-auto h-[70vh]">
                  <table className="w-full text-sm text-left text-gray-600">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0 border rounded-lg">
                      <tr>
                        <th className="px-6 py-3 sticky top-0">S. No.</th>
                        <th className="px-6 py-3 sticky top-0">Partner ID</th>
                        <th className="px-6 py-3 sticky top-0">Reference ID</th>
                        <th className="px-6 py-3 sticky top-0 w-1/2">
                          Comments
                        </th>
                        <th className="px-6 py-3 sticky top-0 text-center">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="border rounded">
                      {currentWorkflowData.length > 0 ? (
                        currentWorkflowData.map((item, i) => (
                          <tr
                            key={i}
                            className="bg-white border-b hover:bg-gray-50"
                          >
                            <td className="px-6 py-4">
                              {startIndexWorkflow + i + 1}
                            </td>
                            <td className="px-6 py-4">{item.partnerId}</td>
                            <td className="px-6 py-4">{item.referenceId}</td>
                            <td className="px-6 py-4">
                              <ul className="space-y-1">
                                {String(item.comments || "")
                                  .split("\n")
                                  .map((line, idx) => (
                                    <li key={idx}>{line}</li>
                                  ))}
                              </ul>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() =>
                                    requestActionConfirmation(
                                      item,
                                      "Ignore",
                                      i,
                                      "rules"
                                    )
                                  }
                                  disabled={isUpdating}
                                  className="p-1 rounded-md border disabled:opacity-50"
                                  title="Ignore"
                                >
                                  Ignore
                                </button>
                                <button
                                  onClick={() =>
                                    requestActionConfirmation(
                                      item,
                                      "Close",
                                      i,
                                      "rules"
                                    )
                                  }
                                  disabled={isUpdating}
                                  className="p-1 rounded-md border disabled:opacity-50"
                                  title="Close"
                                >
                                  Close
                                </button>
                                <button
                                  onClick={() =>
                                    requestActionConfirmation(
                                      item,
                                      "Incorrect",
                                      i,
                                      "rules"
                                    )
                                  }
                                  disabled={isUpdating}
                                  className="p-1 rounded-md border disabled:opacity-50"
                                  title="Incorrect"
                                >
                                  Incorrect
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center py-4">
                            No workflow abnormalities found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {confirmModalOpen && pendingAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
            <h2 className="text-lg font-semibold mb-4">Confirm Action</h2>
            <p className="mb-4">
              Are you sure you want to perform the{" "}
              <strong>{pendingAction.action}</strong> action on{" "}
              {pendingAction.type === "rules" ? (
                <span>
                  reference ID <strong>{pendingAction.item.referenceId}</strong>
                </span>
              ) : (
                <span>
                  partner ID <strong>{pendingAction.item.partnerId}</strong>
                </span>
              )}
              ?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setConfirmModalOpen(false);
                  setPendingAction(null);
                }}
                className="px-2 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300"
              >
                No
              </button>
              <button
                onClick={async () => {
                  setConfirmModalOpen(false);
                  if (pendingAction.type === "abnormality") {
                    await handleVolumeActionClick(
                      pendingAction.item,
                      pendingAction.action,
                      pendingAction.index
                    );
                  } else {
                    await handleActionClick(
                      pendingAction.item,
                      pendingAction.action,
                      pendingAction.index
                    );
                  }
                  setPendingAction(null);
                }}
                className="px-2 py-1 text-sm rounded bg-[#7417B3] text-white hover:bg-[#610b9a]"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {isVolumeModalOpen && (
        <div className="fixed inset-0 flex justify-end items-center bg-black bg-opacity-50 z-30 ">
          <div className="bg-white w-[60vw] h-full p-6 shadow-lg">
            <div className="space-y-4">
              <div className="space-y-4">
                <p className="text-gray-500 font-medium">
                  Volume Validation Rules:
                </p>
                <textarea
                  rows={6}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base border text-gray-900 outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:outline-indigo-600 sm:text-sm/6"
                  placeholder="Enter Rules for Volume Validation"
                  onChange={(e) => handleChange(e, "volume")}
                />
                <div className="flex justify-start gap-4 mt-2">
                  <button
                    className="px-2 py-1 text-sm rounded bg-white text-gray-500 border hover:bg-gray-50"
                    onClick={() => closeSidebar("volume")}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleSubmittingRules("volume")}
                    disabled={isPostingRules}
                    className="px-2 py-1 text-sm rounded bg-[#7417B3] text-white hover:bg-[#610b9a]"
                  >
                    {isPostingRules ? (
                      <span className="animate-pulse">Saving ...</span>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
                <div>
                  {rulesResponse.label === activeTab ? (
                    <div>
                      {rulesResponse.status === "success" ? (
                        <span className="text-green-600">
                          Success : {rulesResponse.resp?.message || "OK"}
                        </span>
                      ) : rulesResponse.status === "failed" ? (
                        <span className="text-red-500">
                          Failed : {rulesResponse.resp}
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={() => closeSidebar("volume")}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 transition hover:shadow-lg active:scale-95"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {isEventModalOpen && (
        <div className="fixed inset-0 z-30 flex justify-end bg-black bg-opacity-50">
          <div className="bg-white w-[60vw] h-full p-6 shadow-lg">
            <div className="space-y-4">
              <div className="space-y-4">
                <p className="text-gray-500 font-medium">
                  Event Validation Rules:
                </p>
                <textarea
                  rows={6}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base border text-gray-900 outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:outline-indigo-600 sm:text-sm/6"
                  placeholder="Enter Rules for Event Validation"
                  onChange={(e) => handleChange(e, "event")}
                />
                <div className="flex justify-start gap-4 mt-2">
                  <button
                    className="px-2 py-1 text-sm rounded bg-white text-gray-500 border hover:bg-gray-50"
                    onClick={() => closeSidebar("event")}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleSubmittingRules("event")}
                    disabled={isPostingRules}
                    className="px-2 py-1 text-sm rounded bg-[#7417B3] text-white hover:bg-[#610b9a]"
                  >
                    {isPostingRules ? (
                      <span className="animate-pulse">Saving ...</span>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
                {rulesResponse.label === "event" ? (
                  <div>
                    {rulesResponse.status === "success" ? (
                      <span className="text-green-500">
                        Success : {rulesResponse.resp?.message || "OK"}
                      </span>
                    ) : rulesResponse.status === "failed" ? (
                      <span className="text-red-500">
                        Failed : {rulesResponse.resp}
                      </span>
                    ) : (
                      ""
                    )}
                  </div>
                ) : (
                  ""
                )}
              </div>
            </div>
            <button
              onClick={() => closeSidebar("event")}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 transition hover:shadow-lg"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainContent;


